import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  FiMenu, FiLogOut, FiPlus, FiFolder, FiCheckSquare,
  FiCalendar, FiBarChart2, FiSearch, FiSettings, FiUser,
  FiMail, FiChevronDown
} from 'react-icons/fi';
import NotificationBell from '../Communication/NotificationBell';

const Header = ({ toggleSidebar }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const quickLinks = [
    { icon: FiPlus, label: 'New Project', path: '/app/projects/new', color: 'text-blue-600 hover:bg-blue-50' },
    { icon: FiCheckSquare, label: 'New Task', path: '/app/tasks/new', color: 'text-green-600 ' },
    { icon: FiCalendar, label: 'Meetings', path: '/app/meetings', color: 'text-purple-600 hover:bg-purple-50' },
    { icon: FiBarChart2, label: 'Reports', path: '/app/reports', color: 'text-orange-600 hover:bg-orange-50' },
    { icon: FiSettings, label: 'Settings', path: '/app/settings', color: 'text-gray-600 hover:bg-gray-50' },
  ];

  const userMenuItems = [
    { icon: FiUser, label: 'My Profile', action: () => navigate('/app/settings') },
    { icon: FiSettings, label: 'Account Settings', action: () => navigate('/app/settings') },
    { icon: FiMail, label: 'Messages', action: () => navigate('/app/chat') },
    { icon: FiLogOut, label: 'Logout', action: logout, color: 'text-red-600 custom-hover:bg-red-50' },
  ];

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  return (
    <header className="bg-white shadow-md">
      <div className="flex items-center justify-between px-6 py-4">
        {/* Left Section - Mobile Menu */}
        <button onClick={toggleSidebar} className="text-gray-500 focus:outline-none md:hidden">
          <FiMenu className="w-6 h-6" />
        </button>

        {/* Center Section - Quick Links (Hidden on mobile) */}
        <div className="hidden md:flex items-center space-x-2 flex-1 justify-center">
          {quickLinks.map((link, index) => (
            <button
              key={index}
              onClick={() => navigate(link.path)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${link.color}`}
              title={link.label}
            >
              <link.icon className="w-5 h-5" />
              <span className="text-sm font-medium hidden lg:inline">{link.label}</span>
            </button>
          ))}
        </div>
        
        {/* Right Section - User Info */}
        <div className="flex items-center space-x-4">
          <NotificationBell />
          
          {/* User Menu Dropdown */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-3 hover:bg-gray-50 rounded-lg px-3 py-2 transition-colors"
            >
              {/* User Avatar */}
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-blue-500 flex items-center justify-center text-white font-semibold shadow-md">
                           {}
                            {user?.avatar_url ? (
                              <img
                                src={user.avatar_url}
                                alt={`${user.first_name} ${user.last_name}`}
                                className="w-full h-full rounded-full object-cover"
                              />
                            ) : (
                              <>
                                {user?.first_name?.[0]}
                                {user?.last_name?.[0]}
                              </>
                            )}
              </div>
              
              {/* User Info - Hidden on mobile */}
              <div className="text-left hidden sm:block">
                <p className="text-sm font-medium text-gray-700">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs text-gray-500">{user?.role?.name} - ({user?.user_code})</p>
              </div>
              
              {/* Dropdown Icon */}
              <FiChevronDown
                className={`w-4 h-4 text-gray-500 transition-transform hidden sm:block ${
                  showUserMenu ? 'transform rotate-180' : ''
                }`}
              />
            </button>

            {/* Dropdown Menu */}
            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                {/* User Info in Dropdown - Visible on mobile */}
                <div className="px-4 py-3 border-b border-gray-200 sm:hidden">
                  <p className="text-sm font-medium text-gray-700">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                  <p className="text-xs text-gray-500 mt-1">{user?.role?.name} - {user?.user_code}</p>
                </div>

                {/* Menu Items */}
                {userMenuItems.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      item.action();
                      setShowUserMenu(false);
                    }}
                    className={`w-full flex items-center space-x-3 px-4 py-2 text-sm transition-colors ${
                      item.color || 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
